import { useCallback, useEffect, useRef, useState } from "react";
import { useGetDataMutation } from "./dataApiSlice";
import {
  activeSheetFilters,
  getL1SheetProperty,
  getL2SheetProperty,
  getWorkbookProperty,
} from "./WorkbookSlice";
import { useSelector, useDispatch } from "react-redux";
import { Aside3FilterSidebar } from "./components/Aside3Components/Aside3FilterSidebar";
import { buildQuery } from "./utils/buildQuery";
import ChartsComp from "./components/Aside3Components/ChartsComp";
import { useMyContext } from "../../MyContext";
import {
  getSheetControls,
  getSheetsWithActiveIndexes,
  setSheetControls,
} from "./WorkbookControllingSlice";
import LoadingIndicator from "../../components/BasicComponents/LoadingIndicator";
import VizardLoader from "./vizard/VizardLoader";
import { getChartDetails } from "../../middleware/getVizDetails";
import {
  downloadDataAsCSV,
  takeAndDownloadScreenshotForEchart,
  takeAndDownloadScreenshotForRechart,
} from "../smallFunction/SmallFunctions";
import useAuth from "../../hooks/useAuth";

export default function Aside3({
  currentSheet,
  SheeType,
  vizType: vizTypeFromWorkbook,
  isSheetInDashboard,
  dashboardAnimation,
  workbookType,
  widthHeightObject,
  isVizardLoading,
  isVizardError,
  vizardError,
  screenshotFunction,
  setToast,
}) {
  const { functionCalled, targetId } = useMyContext();
  const componentRef = useRef(null);
  const dispatch = useDispatch();
  const { username } = useAuth();
  // console.log(isSheetInDashboard);
  const { SheetName, Sheetid } = currentSheet;
  const Sheetindexvalue = Number(currentSheet.index);
  const rowKeysRef = useRef([]);
  const groupByKeysRef = useRef([]);
  const valueKeysRef = useRef([]);
  const toolTipKeysRef = useRef([]);

  // console.log("aside 3 running");

  const sheetsWithActivity = useSelector((state) =>
    isSheetInDashboard ? [] : getSheetsWithActiveIndexes(state)
  );

  const activeSheetId =
    sheetsWithActivity.length === 1 ? sheetsWithActivity[0].Sheetid : null;

  const nonActiveSheetFilters = useSelector((state) =>
    isSheetInDashboard || !activeSheetId
      ? []
      : activeSheetFilters(state, activeSheetId)
  );

  const [getData, { data, isLoading, isSuccess, isError, error }] =
    useGetDataMutation();
  const workbook_ID = useSelector((state) => getWorkbookProperty(state, "_id"));

  const queryObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "query")
  );
  const filterArray = useSelector((state) =>
    getL2SheetProperty(state, Sheetid, "query", "filters")
  );
  const sheetType = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "sheetType")
  );
  const rowLimit = useSelector((state) =>
    getL2SheetProperty(state, Sheetid, "query", "rowLimit")
  );
  const offset = useSelector((state) =>
    getL2SheetProperty(state, Sheetid, "query", "offset")
  );
  const dataSourceID = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "dataSourceID")
  );
  const vizType = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "VizType")
  );
  const commonFilters = useSelector((state) =>
    getWorkbookProperty(state, "commonFilters")
  );
  const isAnimataionOff = useSelector(
    getSheetControls({ Sheetid, key: "isAnimataionOff" })
  );

  const sortProperties = useSelector((state) => {
    return getL2SheetProperty(state, Sheetid, "actions", "sort");
  });

  const queryDataControl = useSelector(
    getSheetControls({ Sheetid, key: "isPause" })
  );

  // // console.log(queryDataControl);

  const handleQueryData = useCallback(
    (queryBody) => {
      if (!queryDataControl) {
        // // console.log(queryBody);
        getData(queryBody);
        if (!isSheetInDashboard) {
          dispatch(
            setSheetControls({
              Sheetid: Sheetid,
              controls: { queryBody },
              calledFrom: "aside 3",
            })
          );
        }
      }
    },
    [queryDataControl, getData]
  );

  useEffect(() => {
    const sheetDataSourceID = dataSourceID;
    // console.log(sheetDataSourceID);
    if (queryObject) {
      const { queryBody, rowKeys, groupByKeys, valueKeys, toolTipKeys } =
        buildQuery(
          queryObject,
          dataSourceID,
          commonFilters,
          sortProperties,
          rowLimit,
          vizType,
          offset,
          username
        );
      // console.log("queryBody is running", commonFilters);

      rowKeysRef.current = rowKeys;
      groupByKeysRef.current = groupByKeys;
      valueKeysRef.current = valueKeys;
      toolTipKeysRef.current = toolTipKeys;
      handleQueryData(queryBody);
    }
  }, [
    queryObject,
    Sheetindexvalue,
    commonFilters,
    dataSourceID,
    handleQueryData,
    sortProperties,
    rowLimit,
  ]);
  const rowKeys = rowKeysRef.current;
  const groupByKeys = groupByKeysRef.current;
  const valueKeys = valueKeysRef.current;
  const toolTipKeys = toolTipKeysRef.current;

  useEffect(() => {
    if (data && data.length === 500) {
      setToast({
        message:
          "Data is reached of limit 500 please apply filters for better understanding",
        status: "error",
      });
    }
  }, [data]);
  let vizContent;
  if (isVizardLoading) {
    vizContent = <VizardLoader />;
  } else if (isVizardError) {
    vizContent = (
      <main className="text-red-500 font-medium">
        {SheetName}: {vizardError?.["data"]?.["message"] || "An error occurred"}
      </main>
    );
  } else if (isLoading) {
    vizContent = (
      <main className="w-full h-full">
        {SheetName} : <LoadingIndicator />
      </main>
    );
  } else if (isError) {
    // console.log(error);
    const errorMessage = "An error occurred";
    vizContent = (
      <main className="text-red-500 font-medium">
        {SheetName}: {errorMessage}
      </main>
    );
  } else if (isSuccess) {
    // console.log(queryObject);
    vizContent = (
      <ChartsComp
        {...{
          Sheetid,
          data,
          rowKeys,
          groupByKeys,
          valueKeys,
          toolTipKeys,
          vizType,
          sortProperties,
          isSheetInDashboard,
          isAnimataionOff: isSheetInDashboard
            ? dashboardAnimation
            : isAnimataionOff,
          dataSourceID,
          filters: queryObject?.filters ? queryObject?.filters : [],
          commonFilters,
          widthHeightObject,
          rowLimit,
          offset: offset ?? 0,
        }}
      />
    );
  }

  function chartType(VizType) {
    const chartDetails = getChartDetails(VizType);
    return chartDetails?.chartType;
  }

  useEffect(() => {
    if (functionCalled?.downloadType === "csv" && Sheetid === targetId) {
      downloadDataAsCSV(data, SheetName);
    } else if (functionCalled?.downloadType === "image") {
      setTimeout(() => {
        if (chartType(vizType) === "echarts") {
          takeAndDownloadScreenshotForEchart(
            functionCalled?.imageFormat,
            componentRef,
            SheetName,
            sheetType
          );
        } else {
          takeAndDownloadScreenshotForRechart(
            screenshotFunction,
            Sheetid,
            functionCalled?.imageFormat,
            workbook_ID,
            sheetType,
            nonActiveSheetFilters
          );
        }
      }, 100); // Delay slightly to allow DOM rendering
    }
  }, [functionCalled, data]);

  const isAnyVisibleFilterTrue =
    filterArray !== undefined && filterArray.some((item) => item.visibleFilter);

  // Define conditionals beforehand
  const isTableType = ["simpleTable", "table"].includes(vizType);
  const isPublishedTable = isTableType && workbookType === "published";
  const showFilterSidebar = isAnyVisibleFilterTrue && !isSheetInDashboard;
  const isChart = sheetType === "chart";

  const mainHeight = isPublishedTable ? "h-[90%]" : "h-full";
  const contentWidth = isTableType
    ? isAnyVisibleFilterTrue
      ? isSheetInDashboard
        ? "w-full flex flex-col"
        : "w-[85%] flex flex-col"
      : "w-full flex flex-col"
    : "w-full flex";

  const filterSidebarWidth = isTableType ? "w-[15%]" : "w-[12vw]";

  return (
    <main
      className={`w-full flex items-center justify-between h-full bg-transparent`}
    >
      <div
        ref={componentRef}
        id="aside3Chart"
        className={`${contentWidth} ${mainHeight} h-full items-start justify-between p-[2px] bg-transparent gap-0 relative`}
      >
        {vizContent}
      </div>
      {showFilterSidebar && (
        <div className={`relative right-0 h-full ${filterSidebarWidth}`}>
          {isChart && (
            <Aside3FilterSidebar
              Sheetid={Sheetid}
              isFilterVisibleInDashboard={false}
              filterArray={filterArray}
              commonFilters={commonFilters}
            />
          )}
        </div>
      )}
    </main>
  );
}
